/**
 * Convert a hex color to bare HSL components for Tailwind CSS.
 * Tailwind's opacity modifier system (e.g. bg-primary/10) requires
 * bare "H S% L%" format without the hsl() wrapper.
 *
 * @param {String} hexColor hex color string (e.g. '#7c3aed')
 * @returns {String} bare HSL components (e.g. '263 84% 58%')
 */
const hexToHslComponents = hexColor => {
  let hex = hexColor.replace(/^#/, '');
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }
  const r = parseInt(hex.substr(0, 2), 16) / 255;
  const g = parseInt(hex.substr(2, 2), 16) / 255;
  const b = parseInt(hex.substr(4, 2), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);

  let h;
  let s;
  let l = (max + min) / 2;

  if (max === min) {
    h = 0;
    s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  h = Math.round(h * 360);
  s = Math.round(s * 100);
  l = Math.round(l * 100);

  return `${h} ${s}% ${l}%`;
};

/**
 * Construct a list of custom CSS Properties from branding config.
 *
 * @param {Object} brandingConfig branding configuration
 * @returns Object literal containing custom CSS Properties (e.g. ['--marketplaceColor']: #aaff00).
 */
export const getCustomCSSPropertiesFromConfig = brandingConfig => {
  const marketplaceColorsMaybe = brandingConfig.marketplaceColor
    ? {
        ['--marketplaceColor']: brandingConfig.marketplaceColor,
        ['--marketplaceColorDark']: brandingConfig.marketplaceColorDark,
        ['--marketplaceColorLight']: brandingConfig.marketplaceColorLight,
      }
    : {};
  const primaryButtonColorsMaybe = brandingConfig.colorPrimaryButton
    ? {
        ['--colorPrimaryButton']: brandingConfig.colorPrimaryButton,
        ['--colorPrimaryButtonDark']: brandingConfig.colorPrimaryButtonDark,
        ['--colorPrimaryButtonLight']: brandingConfig.colorPrimaryButtonLight,
      }
    : {};

  // Bridge marketplace color into Tailwind/shadcn CSS variable system.
  // Tailwind expects bare HSL components (e.g. "22.65 86.44% 53.73%") so that
  // opacity modifiers like bg-primary/10 work correctly.
  // Force ActivityFox orange (#E8681A) — Console branding color is applied
  // to --marketplaceColor above but --primary is the Replit design system token.
  const activityFoxPrimary = '#E8681A';
  const tailwindColorsMaybe = {
    ['--primary']: hexToHslComponents(activityFoxPrimary),
    ['--ring']: hexToHslComponents(activityFoxPrimary),
    ['--sidebar-primary']: hexToHslComponents(activityFoxPrimary),
  };

  return { ...marketplaceColorsMaybe, ...primaryButtonColorsMaybe, ...tailwindColorsMaybe };
};

/**
 * This includes Custom CSS Properties, which are defined in hosted asset: branding.json
 *
 * @param {Object} brandingConfig branding configuration
 * @param {Node} element DOM element, which gets these CSS vars included.
 */
export const includeCSSProperties = (brandingConfig, element) => {
  Object.entries(getCustomCSSPropertiesFromConfig(brandingConfig)).forEach(customCSSProperty => {
    const [key, value] = customCSSProperty;
    element.style.setProperty(key, value);
  });
};
